import COUNTRIES from '@appscore/web-constants/countries';
import {
  getSentryDSN,
  SENTRY_ENVIRONMENTS,
} from '@commons/utils/sentryConstants';
import { getEnvironment } from '@commons/config';
import applicationVersion from '@commons/config/applicationVersion';
import applicationName from '@commons/config/applicationName';
import { PROD_ENVIRONMENT } from '@commons/config/constants';
import { defaultStackParser, makeFetchTransport } from '@sentry/browser';
import * as Sentry from '@sentry/browser';
import queryString from 'query-string';
import isChunkLoadError from './utils/isChunkLoadError';
import groupTransactions from './groupTransactions';

const uiEvents = ['ui.input', 'ui.click'];

function beforeBreadcrumb(breadcrumb) {
  return uiEvents.includes(breadcrumb.category) ? null : breadcrumb;
}

function beforeSend(event, hint) {
  const error = hint && hint.originalException;

  if (isChunkLoadError(error?.message)) {
    return null;
  }

  if (
    error &&
    error.isAxiosError &&
    error.response &&
    error.response.status === 403
  ) {
    return null;
  }
  if (error && error.isAxiosError && error.code === 'ECONNABORTED') {
    return null;
  }
  return event;
}

function getCountriesUrls(env) {
  return COUNTRIES.map(country => {
    switch (env) {
      case 'live':
        return country.live;
      case 'staging':
      case 'stg':
        return country.staging;
      case 'local':
      default:
        return country.local();
    }
  });
}

export default async function initializeSentry(
  sessionId,
  { user, countryName },
) {
  const environment = getEnvironment();
  const sampleRate = environment === PROD_ENVIRONMENT ? 0.1 : 1;
  const isSentryEnabled = SENTRY_ENVIRONMENTS.includes(environment);
  if (isSentryEnabled) {
    const SENTRY_DSN = getSentryDSN(environment);

    const {
      location: { pathname, search },
    } = window;

    const parsedSearch = queryString.parse(search);

    Sentry.init({
      dsn: SENTRY_DSN,
      integrations: [
        Sentry.browserTracingIntegration({
          beforeStartSpan: context => ({
            ...context,
            name: groupTransactions(pathname, parsedSearch),
          }),
        }),
      ],
      environment,
      release: `${applicationName}@${applicationVersion}`,
      tracesSampleRate: sampleRate,
      tracePropagationTargets: getCountriesUrls(environment),
      stackParser: defaultStackParser,
      beforeBreadcrumb,
      beforeSend,
      maxBreadcrumbs: 50,
      sampleRate,
      transport: makeFetchTransport,
      allowUrls: getCountriesUrls(environment),
    });

    Sentry.setTag('country', countryName);
    Sentry.setTag('sessionId', sessionId);
    Sentry.setUser(user);

    return Sentry;
  }

  return null;
}
